import type { ServiceResponse } from "./api.types";
import { api } from "./axios";

export interface InsuranceGroupOption {
  name: string;
  displayName: string;
  groupId: string;
}

export const resolveGroupFromName = (
  name: string,
  groups: InsuranceGroupOption[],
): InsuranceGroupOption | undefined => {
  if (name === "") {
    return undefined;
  }
  return groups.find((g) => g.name === name || g.displayName === name);
};

interface InsuranceGroupsResponse {
  groups: string[];
  groupOptions: InsuranceGroupOption[];
}

export const getInsuranceGroups = async (): Promise<InsuranceGroupOption[]> => {
  const { data } = await api.get<ServiceResponse<InsuranceGroupsResponse>>(
    "backoffice/insurance-groups",
  );
  return data.data.groupOptions;
};

// --- Staff (patient-less) search endpoints ---

export interface StaffProviderMetadata {
  specialties: string[];
  insurancesCantSee: string[];
  biography: string;
}

// Mirrors the patient-facing `Provider` type (see `~/api/providers`), plus the
// composite-ranking fields the backend already computes (isFullMatch,
// bookedMinutes, sortOrder) so the preview can render providers in backend
// (composite) order without re-deriving it client-side.
export interface StaffProvider {
  id: string;
  fullName: string;
  availableSlots: string[];
  metadata: StaffProviderMetadata;
  licensedIn: string[];
  avatarUrl: string | null;
  credentials: string;
  isFullTime: boolean;
  matchCount: number;
  isFullMatch: boolean;
  bookedMinutes: number;
  sortOrder: number;
  // Target the ranking held this provider to for the searched pay period, in
  // minutes. Null when the period lies outside the retained booked-minutes window.
  periodCapMinutes: number | null;
  isAtCap: boolean;
  // "Offer first" on the ranking page: lifted to the top of the under-target group.
  isPrioritized: boolean;
}

interface StaffSearchParams {
  groupId: string;
  conditions: string[];
  state: string;
  preferredOfficeVisit: string;
  startDate: string;
  endDate: string;
  timezone: string;
}

export const staffSearch = async (
  params: StaffSearchParams,
): Promise<StaffProvider[]> => {
  const { data } = await api.get<ServiceResponse<StaffProvider[]>>(
    "backoffice/providers/staff-search",
    { params },
  );
  return data.data;
};

export interface StaffDaysWithAvailability {
  dates: Record<string, number>;
  // Note: unlike the patient flow's `availabilityForNextMonth` (which reflects
  // a next-month fallback search), for staff this simply means "the requested
  // date window has any availability at all" — there is no fallback search.
  availabilityForNextMonth: boolean;
}

export const staffWeekAvailability = async (
  params: StaffSearchParams,
): Promise<StaffDaysWithAvailability> => {
  const { data } = await api.get<ServiceResponse<StaffDaysWithAvailability>>(
    "backoffice/providers/staff-week-availability",
    { params },
  );
  return data.data;
};
